/** @jsxImportSource @emotion/react */
import {
  ComponentType,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useMerchantApplicationContext, useSnackbar } from 'contexts';
import { PageAction, PageRoute } from 'enums';
import {
  isAbortError,
  isFieldError,
  isFormError,
  MappedError,
} from 'types/common/ApiHelpers';
import type {
  ActivationProgressStatus,
  MerchantApplicationProgressResponse,
} from 'types/responses/activation';
import { StepComponentProps } from 'types/interfaces/sign-up/MerchantApplication.types';
import {
  ActivationStepKey,
  getActivationStepIndex,
  getNextActivationStepKey,
  getPreviousActivationStepKey,
} from './ActivationConfig';
import {
  ActivationBusinessDetailsPatch,
  ActivationLocationDetailsProps,
  ActivationPatchPayload,
  ActivationStepData,
  compactDeep,
} from './steps/activationStepData';

import {
  LocationDetailsStepModule,
  ProvidedServicesStepModule,
  TransactionDetailsStepModule,
} from './steps';
import { withActivationCommonLayout } from 'components/activation';
import { logError, logEvent, useActivationFunctions } from 'utils';
import { useNavigate } from 'react-router-dom';

export const ACTIVATION_STEP_COMPONENTS_MAP: Record<
  ActivationStepKey,
  ComponentType<StepComponentProps>
> = {
  [ActivationStepKey.LocationDetails]: withActivationCommonLayout(
    LocationDetailsStepModule,
    { showPageWrapper: false }
  ),
  [ActivationStepKey.TransactionDetails]: withActivationCommonLayout(
    TransactionDetailsStepModule,
    { showPageWrapper: false }
  ),
  [ActivationStepKey.ProvidedServices]: withActivationCommonLayout(
    ProvidedServicesStepModule
  ),
};

export const buildActivationPatchPayload = (
  stepDetails: ActivationStepData
): ActivationPatchPayload => {
  const locationDetailsForPatch = {
    ...((stepDetails[ActivationStepKey.LocationDetails] ??
      {}) as ActivationLocationDetailsProps),
  };
  delete locationDetailsForPatch.businessAddressDisplay;

  const businessDetails = compactDeep({
    ...locationDetailsForPatch,
    ...(stepDetails[ActivationStepKey.TransactionDetails] ?? {}),
    ...(stepDetails[ActivationStepKey.ProvidedServices] ?? {}),
  }) as ActivationBusinessDetailsPatch | undefined;

  return (compactDeep({
    businessDetails,
  }) ?? {}) as ActivationPatchPayload;
};

const BUSINESS_DETAILS_PROGRESS_BY_STEP: Record<
  ActivationStepKey,
  ActivationProgressStatus
> = {
  [ActivationStepKey.LocationDetails]: 'EditStepB',
  [ActivationStepKey.TransactionDetails]: 'EditStepC',
  [ActivationStepKey.ProvidedServices]: 'Confirmed',
};

export const buildActivationProgressPayload = (
  activeStepKey: ActivationStepKey,
  progress: MerchantApplicationProgressResponse
): MerchantApplicationProgressResponse => ({
  businessDetails: BUSINESS_DETAILS_PROGRESS_BY_STEP[activeStepKey],
  directorDetails: progress.directorDetails,
  settlementDetails: progress.settlementDetails,
  // Always carry both flags forward so a partial PATCH never overwrites a
  // previously confirmed flag with false. Only override the flag for the
  // step currently being completed.
  businessDetailsStepAAIConfirmed:
    activeStepKey === ActivationStepKey.LocationDetails
      ? true
      : progress.businessDetailsStepAAIConfirmed,
  businessDetailsStepBAIConfirmed:
    activeStepKey === ActivationStepKey.TransactionDetails
      ? true
      : progress.businessDetailsStepBAIConfirmed,
  businessDetailsStepCAIConfirmed:
    activeStepKey === ActivationStepKey.ProvidedServices
      ? true
      : progress.businessDetailsStepCAIConfirmed,
});

type ActivationFlowProps = {
  activeStepKey: ActivationStepKey;
  onStepChange: (stepKey: ActivationStepKey) => void;
  onComplete: () => void | Promise<void>;
};

function ActivationFlow({
  activeStepKey,
  onStepChange,
  onComplete,
}: ActivationFlowProps): JSX.Element {
  const Snackbar = useSnackbar();
  const navigate = useNavigate();
  const {
    activationStepState,
    merchantApplicationId,
    setActivationActiveStep,
    commitActivationStepData,
    mergeActivationData,
    setFormOptions,
    setFormOptionsLoading,
  } = useMerchantApplicationContext();
  const {
    getApplicationFormOptionsAsync,
    getApplicationProgressAsync,
    patchApplicationProgressAsync,
    patchMerchantApplicationAsync,
    mapServerError,
  } = useActivationFunctions();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const submitRef = useRef<(() => Promise<boolean>) | null>(null);
  const hasFetchedFormOptionsRef = useRef(false);
  const fieldErrorHandlerRef = useRef<((error: MappedError) => void) | null>(
    null
  );
  const activeIndex = getActivationStepIndex(activeStepKey);
  const canGoBack = activeIndex > 0;

  const handleReturnToDashboard = () => {
    navigate(PageRoute.Home);
  };

  useEffect(() => {
    setActivationActiveStep(activeStepKey);
  }, [activeStepKey, setActivationActiveStep]);

  useEffect(() => {
    const fetchFormOptions = async (): Promise<void> => {
      if (hasFetchedFormOptionsRef.current) {
        return;
      }

      hasFetchedFormOptionsRef.current = true;

      try {
        const options = await getApplicationFormOptionsAsync();
        setFormOptions(options);
      } finally {
        setFormOptionsLoading(false);
      }
    };

    void fetchFormOptions();
  }, [getApplicationFormOptionsAsync, setFormOptions, setFormOptionsLoading]);

  const ActiveStepComponent = useMemo<ComponentType<StepComponentProps>>(
    () => ACTIVATION_STEP_COMPONENTS_MAP[activeStepKey],
    [activeStepKey]
  );

  const handleError = useCallback(
    (error: MappedError): void => {
      if (isFormError(error)) {
        Snackbar.error(error.message);
      }
    },
    [Snackbar]
  );

  const handleContinueError = useCallback(
    (err: any): void => {
      logError(PageAction.merchantOnboardingPatchActivationInfo, err);

      const mapped = mapServerError(err);

      if (isFieldError(mapped)) {
        fieldErrorHandlerRef.current?.(mapped);
      } else {
        handleError(mapped);
      }
    },
    [handleError, mapServerError]
  );

  const handleProvideSubmit = useCallback(
    (
      fn: () => Promise<boolean>,
      onFieldError?: (error: MappedError) => void
    ) => {
      submitRef.current = fn;
      fieldErrorHandlerRef.current = onFieldError ?? null;
    },
    []
  );

  const goToStep = useCallback(
    (stepKey: ActivationStepKey): void => {
      onStepChange(stepKey);
    },
    [onStepChange]
  );

  const handleContinue = async (): Promise<void> => {
    if (isSubmitting || !submitRef.current) {
      return;
    }

    setIsSubmitting(true);

    try {
      const isValid = await submitRef.current();

      if (!isValid) {
        return;
      }

      const payload = buildActivationPatchPayload(
        activationStepState.stepData.merchantApplicationDetails
      );
      await patchMerchantApplicationAsync(payload);

      const progress = await getApplicationProgressAsync();
      await patchApplicationProgressAsync(
        buildActivationProgressPayload(activeStepKey, progress)
      );

      mergeActivationData({
        ...(activeStepKey === ActivationStepKey.LocationDetails && {
          businessDetailsStepAAIConfirmed: true,
        }),
        ...(activeStepKey === ActivationStepKey.TransactionDetails && {
          businessDetailsStepBAIConfirmed: true,
        }),
        ...(activeStepKey === ActivationStepKey.ProvidedServices && {
          businessDetailsStepCAIConfirmed: true,
        }),
      });
      commitActivationStepData(activeStepKey);

      const nextStepKey = getNextActivationStepKey(activeStepKey);

      if (nextStepKey) {
        goToStep(nextStepKey);
      } else {
        // MC-4775 onComplete is called on ReturnToDashboard and when Submitting
        // Calling it here makes it only log on Submit Business Details
        logEvent(PageAction.merchantOnboardingUAConfirmBusinessDetails, {
          merchantApplicationId,
        });
        await onComplete();
      }
    } catch (error) {
      if (isAbortError(error)) return;

      handleContinueError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = (): void => {
    if (!canGoBack || isSubmitting) return;

    const previousStepKey = getPreviousActivationStepKey(activeStepKey);

    if (previousStepKey) {
      goToStep(previousStepKey);
    }
  };

  return (
    <ActiveStepComponent
      provideSubmit={handleProvideSubmit}
      onError={handleError}
      canGoBack={canGoBack}
      canContinue={!isSubmitting}
      isSubmitting={isSubmitting}
      onBack={handleBack}
      onContinue={handleContinue}
      onReturnToDashboard={handleReturnToDashboard}
    />
  );
}

export default ActivationFlow;
